import { useState } from 'react'
import type { FormEvent } from 'react'
import { toyTallyBump } from '@gridfit/contracts/shapes/toy'
import { asCount } from '@gridfit/core/money'
import { commandHook } from '@/api/hooks'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { markRefused } from './drain'
import { listRepairs, registerRepair } from './repair'
import type { DescribeEntry, RepairComponent } from './repair'

/**
 * Every repair form this app ships registers itself here — via
 * `registerToyRepairs()` below, called once at this module's own top level
 * so importing this file (`repair-coverage.test.ts`'s own `import
 * '@/outbox/repairs.registry'`, and `SyncTray.tsx`'s real import) is enough
 * to register everything, matching the brief's own step 2: "a
 * `repairs.registry.ts` module the shell imports."
 *
 * `registerToyRepairs` is EXPORTED and IDEMPOTENT (fix round 1's Important
 * 2, measured: a bare module-level `registerRepair(...)` call with no
 * exported, re-callable wrapper made `resetRepairsForTests()` a ONE-WAY
 * DOOR — ES modules evaluate top-level code exactly once, so re-importing
 * this file after a reset never re-registers anything). Mirrors
 * `registerToySlice()`'s own established shape (`@gridfit/contracts/toy`):
 * check the registry itself before writing to it, so calling this again —
 * whether from this file's own top level on a second import within the same
 * module graph, or explicitly from a test after `resetRepairsForTests()` —
 * is always safe.
 *
 * `toy.tally.bump` is the ONLY offline-capable command in the repo today
 * (`shapes/toy.ts`'s own comment: "the only shape in the repo that
 * exercises the outbox path until 160 and 170 arrive") — this is Stage B's
 * real repair, the brief's own words, not a placeholder. 160/170/290 add
 * their own commands' repairs here (or in their own sibling registrations
 * this module re-exports) once those commands exist; nothing about this
 * module is toy-specific in shape, only in which one command it currently
 * knows about.
 */
export function registerToyRepairs(): void {
  if (listRepairs().has(toyTallyBump.name)) return
  registerRepair(toyTallyBump.name, ToyTallyBumpRepair, describeToyTallyBump)
}

const useBumpTally = commandHook(toyTallyBump)

/**
 * The "Waiting to send" label (fix round 1's Important 1) — a site manager
 * reading the tray sees "Tally bump: a (+1)", not the developer-facing
 * command identifier `toy.tally.bump`. Falls back to a plain description
 * when the stored payload no longer parses (the SAME defensive case
 * `ToyTallyBumpRepair` below guards against) — a queued row is still worth
 * naming even if its exact fields cannot be shown.
 */
const describeToyTallyBump: DescribeEntry = (entry) => {
  const parsed = toyTallyBump.payload.safeParse(entry.payload)
  return parsed.success ? `Tally bump: ${parsed.data.key} (+${parsed.data.amount})` : 'Tally bump'
}

/**
 * `toy.tally.bump`'s payload is `{ key, amount }`. `key` identifies WHICH
 * tally this command touches — not something a repair should let a user
 * silently redirect to a different tally — so only `amount` is editable
 * here; `key` is shown, not a form field.
 *
 * `entry.payload` is `unknown` (`OutboxEntry`'s own field) — parsed against
 * the SAME zod schema the shape declares (`toyTallyBump.payload`) rather
 * than trusted with a bare cast, so a payload that somehow no longer
 * matches its own command's current shape (a schema change landing between
 * when this was queued and when it is being repaired) is caught here rather
 * than silently forcing a wrong-shaped value onto the wire.
 *
 * OUTCOME HANDLING (fix round 1's Critical 2; web-pre-deploy items 1/2
 * rewrote the `!result.ok` branch, fixed here after being measured —
 * `repair.tsx`'s own `RepairComponent` doc comment has the full reasoning,
 * this is the reference implementation of it):
 *
 *  - `result.ok` (true whether or not `queued`) → `onRepaired()`. The
 *    ORIGINAL bug: this used to gate on `result.ok && !result.queued`,
 *    reasoning that "queued again" was not really fixed — true, but it left
 *    BOTH this row and the new queued resend sitting in the outbox for the
 *    SAME problem, and a user who tapped Resend again (nothing here told
 *    them the first attempt had already queued) enqueued a THIRD entry.
 *    `drain()` cannot dedupe three DIFFERENT idempotency keys — it would
 *    apply the tally bump three times. Measured directly: two `mutateAsync`
 *    calls while offline left two SEPARATE queued rows plus this one still
 *    `refused`, three entries for one correction.
 *  - `!result.ok` (refused again, EVERY reason this time, possibly
 *    different from whatever originally landed this row here) →
 *    persisted through `drain.ts`'s own `markRefused(entry, result.failures,
 *    { resend: true })` — NOT a second, component-local copy of "how a
 *    refusal is recorded". The ORIGINAL bug (web-pre-deploy items 1/2, one
 *    fix, not two): this branch used to hold `result.failures[0]` in a bare
 *    `useState`, which (a) discarded every reason past the first, showing
 *    strictly less than the online path (`test/data-layer.test.tsx`'s
 *    `DraftScreen`) renders for the identical shape, and (b) discarded the
 *    resend's reason ENTIRELY the moment this component unmounted — which
 *    Radix does to everything inside `<SheetContent>` the instant the tray
 *    is closed (`repair.tsx`'s own `RefusedEntry` comment) — leaving nothing
 *    on screen to say a resend had even been attempted, and falling back to
 *    the ORIGINAL entry's now-stale `refusals` on reopen. Routing through
 *    `markRefused` means `RefusedEntry`'s own list (above this form, reading
 *    `entry.refusals` — already plural, already rendering all of them) is
 *    the ONLY place a refusal is shown, online or offline, first attempt or
 *    resend.
 */
const ToyTallyBumpRepair: RepairComponent = ({ entry, onRepaired }) => {
  const parsed = toyTallyBump.payload.safeParse(entry.payload)
  const bump = useBumpTally()
  const [amount, setAmount] = useState<string>(() => (parsed.success ? String(parsed.data.amount) : ''))

  if (!parsed.success) {
    // "Park, never guess" applied to a repair form itself: this entry's
    // stored payload no longer parses against `toy.tally.bump`'s CURRENT
    // schema, so there is nothing safe to pre-fill or resubmit. The plain
    // "Dismiss" button `RefusedEntry` always renders alongside this is
    // still the way out for a row stuck in this state.
    return (
      <p className="text-sm text-destructive">
        This entry&rsquo;s stored payload no longer matches {entry.command}&rsquo;s current shape and cannot be
        repaired here.
      </p>
    )
  }

  const { key } = parsed.data

  const onSubmit = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault()
    const value = Number(amount)
    if (!Number.isInteger(value) || value < 0) return

    const result = await bump.mutateAsync({ key, amount: asCount(value) })
    if (result.ok) {
      onRepaired()
      return
    }
    await markRefused(
      entry,
      result.failures.map((failure) => ({ code: failure.refusal.code, message: failure.refusal.message })),
      { resend: true },
    )
  }

  return (
    <form onSubmit={(event) => void onSubmit(event)} className="flex flex-wrap items-end gap-2">
      <div className="space-y-1">
        <Label htmlFor={`repair-amount-${entry.idempotencyKey}`}>Amount for {key}</Label>
        <Input
          id={`repair-amount-${entry.idempotencyKey}`}
          inputMode="numeric"
          value={amount}
          onChange={(event) => setAmount(event.target.value)}
          className="min-h-[var(--gf-target)] w-24"
        />
      </div>
      <Button type="submit" className="min-h-[var(--gf-target)]">
        Resend
      </Button>
    </form>
  )
}

registerToyRepairs()
